/*LIBRARY*/
import React, { useState, useEffect, useCallback, useRef } from "react";
import { connect } from "react-redux";

/*COMPONENT*/
import MODAL_SEARCH from "../common_modal/MODAL_SEARCH";
import SEARCH_LAYER from "../search_map/SEARCH_LAYER";
import SEARCH_LOCATION from "../search_map/SEARCH_LOCATION";
import SEARCH_INSIDE_LAYER from "../search_map/SEARCH_INSIDE_LAYER";
import MAP_GUIDE from "./MAP_GUIDE";
import MAP_TOP_BAR from "./MAP_TOP_BAR";
import MAP_SIDE_CONTROLS from "./MAP_SIDE_CONTROLS";
import MAP_MODALS from "./MAP_MODALS";
import SCALE_BAR from "./SCALE_BAR";
import FEEDBACK from "./FEEDBACK";

/*REDUX*/
import {
  set_value_layer,
  setEditGeometry,
} from "../../App/actions/layerActions";
import { set_value_properties } from "../../App/actions/propertiesActions";
import { status_action } from "../../App/actions/mapActions";
import { setLanguage } from "../../App/actions/authActions";
import {
  get_property_app,
  get_activities_app,
  get_location_list,
  set_layer_id,
} from "../../App/actions/dataActions";
import { post_activities } from "../../App/actions/activities";

/*PICTURE*/
import close from "../../Assets/svg/close.svg";

/*FUNCTION*/
import generate_uuid from "../../App/validation/generate_uuid";
import get_browser_info from "../../App/validation/get_browser_info";

/*DATA*/
import dict from "../../Data/dict.json";

/*CONST*/
const WAIT_INTERVAL = 1000;
const ENTER_KEY = 13;

const type_to_draw_mode = {
  Polygon: "draw_polygon",
  Point: "draw_point",
  LineString: "draw_line_string",
};

const language_list = [
  {
    name: "INA",
    value: "ina",
    image: "https://mapid.co.id/img/icon/indo-flag.png",
  },
  {
    name: "ENG",
    value: "eng",
    image: "https://mapid.co.id/img/icon/uk-flag.png",
  },
];

const MAP_CONTROL = ({
  auth,
  layer,
  map,
  properties,
  loading_reducer,
  set_value_layer,
  set_value_properties,
  status_action,
  setLanguage,
  get_property_app,
  get_activities_app,
  get_location_list,
  setEditGeometry,
  set_layer_id,
  post_activities,
}) => {
  const timer = useRef(null);
  const compass_ref = useRef(null);

  const {
    map_object,
    tableStatus,
    geometryStatus,
    draw_active,
    geo_layer_list,
    layer_id,
    draw_object,
  } = layer;

  const {
    projection,
    basemap_used,
    sidebar_right_status,
    legend_status,
    is_mobile,
    table_height,
    is_show_property,
    is_show_activity,
    is_show_image,
    is_show_feedback_button,
    mode_search,
    modal_input,
    search_input,
  } = properties;

  const language = localStorage?.language || auth?.language;
  const { loading_item_array } = loading_reducer;

  // --- Core States ---
  const [modal_share, set_modal_share] = useState(false);
  const [copy_status, set_copy_status] = useState(false);
  const [geo_location_status, set_geo_location_status] = useState("pending");
  const [modal_error_gps, set_modal_error_gps] = useState(false);
  const [gps_error_message, set_gps_error_message] = useState("");
  const [browser_info, set_browser_info] = useState({});
  const [error_string, set_error_string] = useState("");
  const [gps_icon_reset, set_gps_icon_reset] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [settingState, setSettingState] = useState({
    modal_setting: false,
    anchor: null,
  });
  const { modal_setting, anchor } = settingState;

  // --- Zoom & Animation States ---
  const [currentZoom, setCurrentZoom] = useState(0);
  const zoomTimeout = useRef(null);
  const zoomRafRef = useRef(null);
  const cinematicActiveRef = useRef(false);
  const stopCinematicRef = useRef(null);

  const [liveTableHeight, setLiveTableHeight] = useState(table_height || 319);
  const heightRafRef = useRef(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const isAnimatingRef = useRef(false);

  const props_ref = useRef({
    layer,
    map,
    properties,
    get_location_list,
    set_value_properties,
    set_value_layer,
  });

  const sidebarClass =
    is_mobile || !sidebar_right_status ? "sidebar_closed" : "";
  const is_community_loading =
    loading_item_array?.includes("get_property_app") ||
    loading_item_array?.includes("get_activities_app");

  let gps_class = "";
  if (geo_location_status === "loading") gps_class = "icon_rotate";
  else if (geo_location_status === "success")
    gps_class = gps_icon_reset ? "icon_reset" : "icon_scale";

  const geo_layer = geo_layer_list?.find(
    (item) => item?.geo_layer?._id === layer_id,
  )?.geo_layer;
  const current_type = geo_layer?.type;

  // --- GUIDE TRIGGER ---
  const startGuide = () => {
    set_value_properties({ key: "showGuideModal", value: false });
    set_value_properties({ key: "guideActive", value: true });
  };

  // --- EFFECTS ---
  useEffect(() => {
    props_ref.current = {
      layer,
      map,
      properties,
      get_location_list,
      set_value_properties,
      set_value_layer,
    };
  });

  useEffect(() => {
    return () => clearTimeout(timer.current);
  }, []);

  useEffect(() => {
    const local_feedback_status = localStorage.getItem(
      "is_show_feedback_button",
    );
    if (local_feedback_status !== null) {
      set_value_properties({
        key: "is_show_feedback_button",
        value: local_feedback_status === "true",
      });
    } else {
      set_value_properties({ key: "is_show_feedback_button", value: true });
    }
  }, [set_value_properties]);

  const update_compass_rotation = useCallback(() => {
    if (map_object && compass_ref.current) {
      const bearing = map_object.getBearing();
      compass_ref.current.style.transform = `rotate(${-bearing}deg)`;
    }
  }, [map_object]);

  useEffect(() => {
    if (!map_object) return;

    map_object.on("rotate", update_compass_rotation);
    setCurrentZoom(map_object.getZoom());

    // OPTIMIZATION: Only update React state on 'zoomend', not 'zoom'.
    // Prevents 60 re-renders/sec during pinch-to-zoom on mobile.
    const handleZoomEnd = () => setCurrentZoom(map_object.getZoom());
    map_object.on("zoomend", handleZoomEnd);

    return () => {
      map_object.off("rotate", update_compass_rotation);
      map_object.off("zoomend", handleZoomEnd);
    };
  }, [map_object, update_compass_rotation]);

  useEffect(() => {
    if (geo_location_status === "success") {
      set_gps_icon_reset(false);
      const timerLocal = setTimeout(() => set_gps_icon_reset(true), 1000);
      return () => clearTimeout(timerLocal);
    } else {
      set_gps_icon_reset(false);
    }
  }, [geo_location_status]);

  // FIX: Consolidated cleanup effect. Cleans up all animation refs and
  // cinematic listeners on unmount without needing map_object in deps.
  useEffect(() => {
    return () => {
      if (zoomTimeout.current) clearTimeout(zoomTimeout.current);
      if (zoomRafRef.current) cancelAnimationFrame(zoomRafRef.current);
      if (stopCinematicRef.current) stopCinematicRef.current();
    };
  }, []);

  useEffect(() => {
    set_browser_info(get_browser_info());
  }, []);

  useEffect(() => {
    setIsAnimating(true);
    isAnimatingRef.current = true;
    const timerLocal = setTimeout(() => {
      setIsAnimating(false);
      isAnimatingRef.current = false;
    }, 350);
    return () => clearTimeout(timerLocal);
  }, [tableStatus, table_height]);

  useEffect(() => {
    if (!tableStatus) return;
    const bottomBar = document.getElementById("bottom_bar_wrapper");
    if (!bottomBar) return;
    const resizeObserver = new ResizeObserver((entries) => {
      if (isAnimatingRef.current) return;
      for (let entry of entries) {
        const newHeight = entry.target.getBoundingClientRect().height;
        if (heightRafRef.current) cancelAnimationFrame(heightRafRef.current);
        heightRafRef.current = requestAnimationFrame(() => {
          setLiveTableHeight(newHeight);
        });
      }
    });
    resizeObserver.observe(bottomBar);
    return () => {
      resizeObserver.disconnect();
      if (heightRafRef.current) cancelAnimationFrame(heightRafRef.current);
    };
  }, [tableStatus]);

  // --- ACTIONS & HANDLERS ---
  const trigger_action = () => {
    const { mode_search, search_input } = props_ref.current.properties;
    if (mode_search === "layer") {
      let layer_id_filtered = [];
      if (!!search_input) {
        const geo_layer_list = props_ref.current.layer.geo_layer_list;
        layer_id_filtered = geo_layer_list
          .filter((item) => {
            const name = item?.geo_layer?.name?.toUpperCase();
            const description = item?.geo_layer?.description?.toUpperCase();
            const input = search_input?.toUpperCase();
            return name.includes(input) || description.includes(input);
          })
          .map((item) => item?.geo_layer?._id);
      }
      props_ref.current.set_value_properties({
        key: "layer_id_filtered",
        value: layer_id_filtered,
      });
    } else {
      if (!!search_input) {
        if (!props_ref.current.map.map_object) return;
        const bbox = props_ref.current.map.map_object.getBounds();
        const minLng = bbox.getWest();
        const minLat = bbox.getSouth();
        const maxLng = bbox.getEast();
        const maxLat = bbox.getNorth();
        const viewbox = `${minLng}%2C${minLat}%2C${maxLng}%2C${maxLat}`;
        props_ref.current.get_location_list({
          search_input: String(search_input).replaceAll(" ", "+"),
          viewbox,
        });
      }
    }
  };

  const on_change_input_search = (e) => {
    clearTimeout(timer.current);
    e.preventDefault();
    set_value_properties({ key: e.target.name, value: e.target.value });
    timer.current = setTimeout(trigger_action, WAIT_INTERVAL);
  };

  const handle_enter = (e) => {
    if (e.keyCode === ENTER_KEY) trigger_action();
  };

  const on_set_value_properties = (key, value) =>
    set_value_properties({ key: key, value: value });
  const on_activate_input_status = () =>
    set_value_properties({ key: "modal_input", value: true });
  const on_deactivate_input_status = () => {
    set_value_properties({ key: "modal_input", value: false });
    set_value_properties({ key: "search_input", value: "" });
  };
  const on_clear_input = () =>
    set_value_properties({ key: "search_input", value: "" });

  const on_create_geometry = () => {
    const draw_mode = type_to_draw_mode?.[current_type] || "";
    if (map_object && draw_mode) {
      if (draw_active) {
        map_object.getCanvas().style.cursor = "";
        if (draw_object) draw_object.changeMode("simple_select");
        set_value_layer({ key: "draw_active", value: false });
      } else {
        map_object.getCanvas().style.cursor = "crosshair";
        if (draw_object) draw_object.changeMode(draw_mode);
        set_value_layer({ key: "draw_active", value: true });
      }
    }
  };

  const on_stop_draw = () => {
    map_object.getCanvas().style.cursor = "";
    if (draw_object) draw_object.changeMode("simple_select");
    set_value_layer({ key: "draw_active", value: false });
  };

  const on_stop_geometry = () => {
    set_layer_id(layer_id);
    setEditGeometry(false);
    map_object.getCanvas().style.cursor = "";
    if (draw_object) draw_object.changeMode("simple_select");
    set_value_layer({ key: "draw_active", value: false });
    status_action();
  };

  const on_delete_geometry = () => {
    if (draw_object && map_object) {
      const feature_id = draw_object.getSelected()?.features?.[0]?.id;
      if (feature_id) draw_object.trash(feature_id);
    }
  };

  const toggle_share_modal = () => {
    set_modal_share(!modal_share);
    set_copy_status(false);
  };

  const shareLink = window.location.href.replace("/editor/", "/view/");

  const handle_copy_link = () => {
    navigator.clipboard.writeText(shareLink).then(() => {
      set_copy_status(true);
      setTimeout(() => set_copy_status(false), 2000);
    });
  };

  // FIX: clearZoomTimers now only cancels the hold-zoom rAF and timeout.
  // No longer references the old animationRef (renamed to zoomRafRef for clarity).
  const clearZoomTimers = useCallback(() => {
    if (zoomTimeout.current) clearTimeout(zoomTimeout.current);
    if (zoomRafRef.current) cancelAnimationFrame(zoomRafRef.current);
  }, []);

  // FIX: Zoom hold animation guarded with map_object.isEasing() check.
  // Prevents the hold-zoom loop from overriding an active pinch gesture.
  const handleZoomInStart = useCallback(() => {
    if (!map_object) return;
    map_object.zoomTo(map_object.getZoom() + 1, { duration: 150 });
    zoomTimeout.current = setTimeout(() => {
      let lastTime = performance.now();
      const animateZoom = (currentTime) => {
        if (map_object.isMoving() && map_object.isZooming()) {
          zoomRafRef.current = requestAnimationFrame(animateZoom);
          return;
        }
        const deltaTime = currentTime - lastTime;
        lastTime = currentTime;
        const zoomDelta = 0.002 * deltaTime;
        map_object.setZoom(map_object.getZoom() + zoomDelta);
        zoomRafRef.current = requestAnimationFrame(animateZoom);
      };
      zoomRafRef.current = requestAnimationFrame(animateZoom);
    }, 300);
  }, [map_object]);

  // FIX: Same guard applied to zoom-out hold.
  const handleZoomOutStart = useCallback(() => {
    if (!map_object) return;
    map_object.zoomTo(map_object.getZoom() - 1, { duration: 150 });
    zoomTimeout.current = setTimeout(() => {
      let lastTime = performance.now();
      const animateZoom = (currentTime) => {
        if (map_object.isMoving() && map_object.isZooming()) {
          zoomRafRef.current = requestAnimationFrame(animateZoom);
          return;
        }
        const deltaTime = currentTime - lastTime;
        lastTime = currentTime;
        const zoomDelta = 0.002 * deltaTime;
        map_object.setZoom(map_object.getZoom() - zoomDelta);
        zoomRafRef.current = requestAnimationFrame(animateZoom);
      };
      zoomRafRef.current = requestAnimationFrame(animateZoom);
    }, 300);
  }, [map_object]);

  const on_geolocate = useCallback(
    (retry_count = 3) => {
      set_geo_location_status("loading");
      set_modal_error_gps(false);
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const uuid = generate_uuid();
            const {
              latitude,
              longitude,
              accuracy,
              altitude,
              altitudeAccuracy,
              heading,
              speed,
            } = position.coords;
            const gps_object = {
              uuid,
              latitude,
              longitude,
              accuracy,
              altitude,
              altitudeAccuracy,
              heading,
              speed,
            };
            set_value_layer({ key: "gps_object", value: gps_object });
            set_geo_location_status("success");
          },
          (error) => {
            let error_message =
              dict?.["Browser GPS retrieval failed, reason unknown"]?.[
                language
              ];
            switch (error.code) {
              case 1:
                error_message =
                  dict?.["Permission denied. Please allow location access."]?.[
                    language
                  ];
                break;
              case 2:
                error_message =
                  dict?.[
                    "Position unavailable. Please check your Wi-Fi connection and try again."
                  ]?.[language];
                break;
              case 3:
                error_message =
                  dict?.["Request timed out. Retrying..."]?.[language];
                break;
              default:
                error_message =
                  dict?.["An unknown error occurred. Please try again."]?.[
                    language
                  ];
            }
            error_message = error?.message || error_message;
            const error_details = {
              name: error.name || "GeolocationError",
              message: error_message,
              code: error.code,
              stack: error.stack || "No stack trace available",
            };
            const generated_error_string = JSON.stringify(error_details);
            if (retry_count > 0 && error.code !== 1) {
              setTimeout(() => on_geolocate(retry_count - 1), 2000);
            } else {
              set_geo_location_status("error");
              set_modal_error_gps(true);
              set_gps_error_message(error_message);
              set_error_string(generated_error_string);
            }
          },
        );
      } else {
        set_geo_location_status("browser_not_supported");
      }
    },
    [language, set_value_layer],
  );

  const toggle_error_gps = () => set_modal_error_gps((prev) => !prev);

  // FIX: on_reset_compass now calls map_object.stop() first to cancel any
  // in-progress easeTo from cinematic mode before applying the new easeTo.
  // Without stop(), the two easeToĀ calls fight each other for one frame.
  const on_reset_compass = useCallback(() => {
    if (!map_object) return;

    // Tear down cinematic mode cleanly first
    if (stopCinematicRef.current) {
      stopCinematicRef.current();
    }

    // Stop any in-progress camera animation before starting a new one
    map_object.stop();

    map_object.easeTo({ pitch: 0, bearing: 0, duration: 1000 });
  }, [map_object]);

  const handleCinematicView = useCallback(() => {
    if (!map_object) return;

    // Tear down any previous cinematic session
    if (stopCinematicRef.current) {
      stopCinematicRef.current();
    }

    cinematicActiveRef.current = true;

    const startZoom = map_object.getZoom();
    const targetZoom = Math.max(0, startZoom - 0.2);

    // Shared cleanup: removes all listeners and marks cinematic as inactive
    const stopCinematic = () => {
      if (!cinematicActiveRef.current) return;
      cinematicActiveRef.current = false;
      stopCinematicRef.current = null;

      map_object.stop();

      map_object.off("moveend", onTiltEnd);
      map_object.off("moveend", onZoomEnd);
      map_object.off("moveend", onRotateEnd);
      map_object.off("mousedown", stopCinematic);
      map_object.off("dragstart", stopCinematic);
      map_object.off("wheel", stopCinematic);
      // touchstart must use the DOM to support passive:true for mobile perf
      map_object
        .getCanvas()
        .removeEventListener("touchstart", stopCinematic, { passive: true });
    };

    stopCinematicRef.current = stopCinematic;

    // Phase 3: Infinite rotation — re-queues itself on each moveend
    // so it appears continuous without any rAF loop.
    const ROTATION_STEP_DEGREES = 120;
    const ROTATION_STEP_DURATION = 8000; // ms per 120° step

    const startRotationStep = () => {
      if (!cinematicActiveRef.current) return;
      const currentBearing = map_object.getBearing();
      map_object.easeTo({
        bearing: currentBearing + ROTATION_STEP_DEGREES,
        duration: ROTATION_STEP_DURATION,
        easing: (t) => t, // linear feels best for slow orbital rotation
      });
    };

    // Phase 2 end → begin rotation
    const onZoomEnd = () => {
      map_object.off("moveend", onZoomEnd);
      if (!cinematicActiveRef.current) return;
      map_object.on("moveend", onRotateEnd);
      startRotationStep();
    };

    // Phase 3 end → queue next rotation step
    const onRotateEnd = () => {
      if (!cinematicActiveRef.current) return;
      startRotationStep();
    };

    // Phase 1 end → begin zoom
    const onTiltEnd = () => {
      map_object.off("moveend", onTiltEnd);
      if (!cinematicActiveRef.current) return;
      map_object.on("moveend", onZoomEnd);
      map_object.easeTo({
        zoom: targetZoom,
        duration: 1000,
        easing: (t) => 1 - Math.pow(1 - t, 3), // ease-out-cubic
      });
    };
    map_object
      .getCanvas()
      .addEventListener("touchstart", stopCinematic, { passive: true });

    map_object.on("mousedown", stopCinematic);
    map_object.on("dragstart", stopCinematic);
    map_object.on("wheel", stopCinematic);

    // Phase 1: tilt to 60°
    map_object.on("moveend", onTiltEnd);
    map_object.easeTo({
      pitch: 60,
      duration: 1200,
      easing: (t) => 1 - Math.pow(1 - t, 3), // ease-out-cubic
    });
  }, [map_object]);

  const on_reload_browser = () => window.location.reload();
  const on_open_setting = (event) =>
    setSettingState((prev) => ({
      ...prev,
      modal_setting: !prev.modal_setting,
      anchor: event?.currentTarget,
    }));
  const on_close_setting = () =>
    setSettingState((prev) => ({ ...prev, modal_setting: false }));
  const on_set_lang = (selected_language) => setLanguage(selected_language);

  const on_set_style_map = (selected_basemap) => {
    const basemap_url = process.env.REACT_APP_MAPID_BASEMAP;
    const basemap_key = process.env.REACT_APP_BASEMAP_KEY;
    let style_url = "";
    switch (selected_basemap) {
      case "2d":
        style_url = `${basemap_url}/styles/street-2d-building/style.json?key=${basemap_key}`;
        break;
      case "3d":
        style_url = `${basemap_url}/styles/basic/style.json?key=${basemap_key}`;
        break;
      case "light":
        style_url = `${basemap_url}/styles/light/style.json?key=${basemap_key}`;
        break;
      case "dark":
        style_url = `${basemap_url}/styles/dark/style.json?key=${basemap_key}`;
        break;
      case "sat":
        style_url = `${basemap_url}/styles/satellite/style.json?key=${basemap_key}`;
        break;
      default:
        style_url = `${basemap_url}/styles/street-2d-building/style.json?key=${basemap_key}`;
        break;
    }
    if (map_object) {
      set_value_properties({ key: "basemap_used", value: selected_basemap });
      map_object.setStyle(style_url);
      map_object.once("style.load", () => {
        map_object.setProjection({ type: projection });
      });
      setTimeout(() => status_action(), 1000);
    }
  };

  const toggle_sidebar_right = () => {
    on_close_setting();
    set_value_properties({
      key: "sidebar_right_status",
      value: !sidebar_right_status,
    });
  };
  const toggle_legend = () =>
    set_value_properties({ key: "legend_status", value: !legend_status });
  const toggle_feedback_setting_button = () => {
    const newValue = is_show_feedback_button === false ? true : false;
    localStorage.setItem("is_show_feedback_button", newValue);
    set_value_properties({ key: "is_show_feedback_button", value: newValue });
    if (!newValue) setShowFeedback(false);
  };

  const on_change_projection = (selected_projection) => {
    set_value_properties({ key: "projection", value: selected_projection });
    if (map_object) map_object.setProjection({ type: selected_projection });
    status_action();
  };

  const on_click_mapid_apps = () => {
    if (!map_object) return;
    const bounds = map_object.getBounds();
    const bboxString = [
      bounds.getWest(),
      bounds.getSouth(),
      bounds.getEast(),
      bounds.getNorth(),
    ].join(",");
    const bboxObject = {
      min_lng: bounds.getWest(),
      min_lat: bounds.getSouth(),
      max_lng: bounds.getEast(),
      max_lat: bounds.getNorth(),
    };
    get_property_app(bboxString);
    get_activities_app(bboxObject, 100);
  };

  const get_dynamic_bottom = (base_bottom) => {
    const base_transition =
      "transform 100ms ease, top 195ms ease-out, right 195ms ease-out";
    const transition = isAnimating
      ? `${base_transition}, bottom 0.3s ease-in-out`
      : base_transition;
    if (!tableStatus) return { bottom: `${base_bottom}px`, transition };
    const desired_offset = liveTableHeight - 99;
    const max_offset =
      typeof window !== "undefined" ? window.innerHeight - 90 - 411 : 1000;
    const offset = Math.min(desired_offset, max_offset);
    return { bottom: `${base_bottom + offset}px`, transition };
  };

  const toggle_show_property = () =>
    set_value_properties({ key: "is_show_property", value: !is_show_property });
  const toggle_show_activity = () =>
    set_value_properties({ key: "is_show_activity", value: !is_show_activity });
  const toggle_show_image = () =>
    set_value_properties({ key: "is_show_image", value: !is_show_image });

  const modal_input_content = modal_input && (
    <MODAL_SEARCH isOpen={modal_input} onClose={on_deactivate_input_status}>
      <div className="box-body" style={{ overflowY: "hidden" }}>
        <div
          onClick={on_activate_input_status}
          className="search_center_content_show background_grey_light"
        >
          <div style={{ display: "flex", flexShrink: 0 }}>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </div>

          <div style={{ flex: 1, display: "flex" }}>
            <input
              type="text"
              onChange={on_change_input_search}
              onKeyDown={handle_enter}
              value={search_input}
              name="search_input"
              placeholder={dict?.["Search"]?.[language]}
              autoFocus
              style={{
                width: "100%",
                background: "transparent",
                border: "none",
                outline: "none",
              }}
            />
          </div>

          {!!search_input && (
            <div style={{ display: "flex", flexShrink: 0 }}>
              <button
                className="button_pill background_grey"
                onClick={on_clear_input}
              >
                {dict?.["clear"]?.[language]}
              </button>
            </div>
          )}
        </div>

        <div className="search_bottom_controls">
          <div style={{ display: "flex", alignItems: "center" }}>
            <button
              onClick={() => on_set_value_properties("mode_search", "layer")}
              className={`button margin_right margin_bottom margin_top ${mode_search === "layer" || mode_search === "search_inside" ? "background_black" : "button_white"}`}
            >
              {dict?.["Data"]?.[language]}
            </button>

            <button
              onClick={() => on_set_value_properties("mode_search", "location")}
              className={`button margin_right margin_bottom margin_top ${mode_search === "location" ? "background_black" : "button_white"}`}
            >
              {dict?.["Location"]?.[language]}
            </button>
          </div>

          <div style={{ display: "flex", alignItems: "center" }}>
            <img
              src={close}
              alt="close"
              height="18px"
              className="cursor_pointer"
              onClick={on_deactivate_input_status}
            />
          </div>
        </div>

        {mode_search === "layer" ? (
          <SEARCH_LAYER
            on_deactivate_input_status={on_deactivate_input_status}
          />
        ) : mode_search === "search_inside" ? (
          <SEARCH_INSIDE_LAYER
            on_deactivate_input_status={on_deactivate_input_status}
          />
        ) : (
          <SEARCH_LOCATION
            on_deactivate_input_status={on_deactivate_input_status}
          />
        )}
      </div>
    </MODAL_SEARCH>
  );

  return (
    <main>
      <MAP_GUIDE />

      <MAP_MODALS
        modal_share={modal_share}
        toggle_share_modal={toggle_share_modal}
        shareLink={shareLink}
        copy_status={copy_status}
        handle_copy_link={handle_copy_link}
        modal_error_gps={modal_error_gps}
        toggle_error_gps={toggle_error_gps}
        dict={dict}
        language={language}
        gps_error_message={gps_error_message}
        error_string={error_string}
        browser_info={browser_info}
        on_reload_browser={on_reload_browser}
        anchor={anchor}
        modal_setting={modal_setting}
        on_close_setting={on_close_setting}
        on_set_style_map={on_set_style_map}
        basemap_used={basemap_used}
        on_change_projection={on_change_projection}
        projection={projection}
        language_list={language_list}
        on_set_lang={on_set_lang}
        legend_status={legend_status}
        toggle_legend={toggle_legend}
        is_show_feedback_button={is_show_feedback_button}
        toggle_feedback_setting_button={toggle_feedback_setting_button}
        startGuide={startGuide}
      />

      {modal_input_content}

      <FEEDBACK showFeedback={showFeedback} setShowFeedback={setShowFeedback} />

      <MAP_TOP_BAR
        auth={auth}
        is_mobile={is_mobile}
        geometryStatus={geometryStatus}
        draw_active={draw_active}
        current_type={current_type}
        on_activate_input_status={on_activate_input_status}
        on_stop_geometry={on_stop_geometry}
        on_create_geometry={on_create_geometry}
        on_delete_geometry={on_delete_geometry}
        on_stop_draw={on_stop_draw}
        toggle_share_modal={toggle_share_modal}
        post_activities={post_activities}
      />

      <MAP_SIDE_CONTROLS
        is_mobile={is_mobile}
        sidebarClass={sidebarClass}
        get_dynamic_bottom={get_dynamic_bottom}
        sidebar_right_status={sidebar_right_status}
        toggle_sidebar_right={toggle_sidebar_right}
        is_community_loading={is_community_loading}
        on_click_mapid_apps={on_click_mapid_apps}
        is_show_activity={is_show_activity}
        toggle_show_activity={toggle_show_activity}
        is_show_image={is_show_image}
        toggle_show_image={toggle_show_image}
        is_show_property={is_show_property}
        toggle_show_property={toggle_show_property}
        on_geolocate={on_geolocate}
        gps_class={gps_class}
        on_reset_compass={on_reset_compass}
        on_cinematic_view={handleCinematicView}
        compass_ref={compass_ref}
        handleZoomInStart={handleZoomInStart}
        handleZoomOutStart={handleZoomOutStart}
        clearZoomTimers={clearZoomTimers}
        currentZoom={currentZoom}
        on_open_setting={on_open_setting}
        is_show_feedback_button={is_show_feedback_button}
        showFeedback={showFeedback}
        setShowFeedback={setShowFeedback}
      />

      <SCALE_BAR layer={layer} />
    </main>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  layer: state.layer,
  map: state.map,
  properties: state.properties,
  loading_reducer: state.loading_reducer,
});

export default connect(mapStateToProps, {
  set_value_layer,
  set_value_properties,
  status_action,
  setLanguage,
  get_property_app,
  get_activities_app,
  get_location_list,
  setEditGeometry,
  set_layer_id,
  post_activities,
})(MAP_CONTROL);
